/**
 * Shared schedule event rendering utilities.
 *
 * Single source of truth for status icons, badges, tooltip HTML, and status
 * classes used by both schedule views:
 *   - Calendar view (schedule-calendar.js) via ES import
 *   - Team Schedule view (_team_schedule_scripts.html) via App.utils.scheduleRendering
 *
 * Any change to how events are visually represented (icon logic, badge rules,
 * tooltip structure, draggable rules) should be made HERE, not in the
 * individual view files.
 */

// ---------------------------------------------------------------------------
// Icon SVG definitions (path data + per-icon color)
// Size class is injected at render time so Calendar (w-3.5) and Team (w-3)
// can share the same definitions.
// ---------------------------------------------------------------------------

const ICON_DEFS = {
    overdue:     { viewBox: '0 0 20 20', color: 'text-red-600',    path: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z' },
    overtime:    { viewBox: '0 0 20 20', color: 'text-orange-500',  path: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z' },
    inProgress:  { viewBox: '0 0 24 24', color: 'text-amber-600',  path: 'M8.6 5.2A1 1 0 0 0 7 6v12a1 1 0 0 0 1.6.8l8-6a1 1 0 0 0 0-1.6l-8-6Z' },
    done:        { viewBox: '0 0 20 20', color: 'text-green-600',   path: 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' },
    absence:     { viewBox: '0 0 20 20', color: 'text-purple-600',  path: 'M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z' },
    breakPause:  { viewBox: '0 0 20 20', color: 'text-orange-500',  path: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z' },
    enRoute:     { viewBox: '0 0 20 20', color: 'text-purple-600',   path: 'M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z' },
    arrived:     { viewBox: '0 0 20 20', color: 'text-cyan-600',    path: 'M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z' },
    conflict:    { viewBox: '0 0 20 20', color: 'text-orange-600',  path: 'M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z' },
    unassigned:  { viewBox: '0 0 20 20', color: 'text-yellow-600',  path: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z' },
    // Slice D — office-only / no-mobile-tracking marker.
    // Phone-with-diagonal-strike glyph: communicates "no mobile
    // app on this visit" at a glance, replacing the former
    // building-office outline (which was easily confused with
    // a generic "office" label). Multi-sub-path SVG → renders
    // with the source's implicit ``nonzero`` fill rule (the
    // default ``evenodd`` would cancel the screen cut-out
    // triangle and the home-button circle, leaving a blank
    // rounded rectangle). Blue tone preserved so the marker
    // stays informational (≠ red/orange alert palette).
    officeOnly:  { viewBox: '0 0 50 50', color: 'text-blue-600',    fillRule: 'nonzero', path: 'M4.90625 2.96875C4.863281 2.976563 4.820313 2.988281 4.78125 3C4.40625 3.066406 4.105469 3.339844 4 3.703125C3.894531 4.070313 4.003906 4.460938 4.28125 4.71875L44.28125 44.71875C44.679688 45.117188 45.320313 45.117188 45.71875 44.71875C46.117188 44.320313 46.117188 43.679688 45.71875 43.28125L38.90625 36.46875L38.90625 8.59375C39.007813 5.492188 36.507813 3 33.40625 3L16.8125 3C13.941406 3 11.257813 5.648438 11.03125 8.59375L5.71875 3.28125C5.511719 3.058594 5.210938 2.945313 4.90625 2.96875 Z M 16 9L34 9L34 31.5625L16 13.5625 Z M 11 14.5L11 41C11 44.699219 14.5 47 17 47L33.40625 47C36.207031 47 38.40625 45.007813 38.90625 42.40625L34 37.5L34 40L16 40L16 19.5 Z M 25 41.5C25.828125 41.5 26.5 42.171875 26.5 43C26.5 43.828125 25.828125 44.5 25 44.5C24.171875 44.5 23.5 43.828125 23.5 43C23.5 42.171875 24.171875 41.5 25 41.5Z' },
};

function renderIcon(key, sizeClass) {
    const d = ICON_DEFS[key];
    // ``fillRule`` defaults to 'evenodd' (the rule that the
    // historical icon set was authored against — overlapping
    // sub-paths cancel into hollow shapes). Icons whose source
    // path was authored with the default ``nonzero`` rule
    // (e.g. ``officeOnly``, where the phone screen + home button
    // are positive shapes that must accumulate over the outer
    // outline) opt out via ``fillRule: 'nonzero'`` in their
    // ICON_DEFS entry.
    const fillRule = d.fillRule || 'evenodd';
    return `<svg class="${sizeClass} flex-shrink-0 ${d.color}" fill="currentColor" viewBox="${d.viewBox}"><path fill-rule="${fillRule}" d="${d.path}" clip-rule="${fillRule}"/></svg>`;
}

// ---------------------------------------------------------------------------
// Time-based status flags (overdue / overtime) — single source of truth
// ---------------------------------------------------------------------------

/**
 * Recompute the time-dependent flags ``isOverdue`` / ``isOvertime`` from
 * an event's status + start / end + tracking flags, using the *browser's*
 * current clock.
 *
 * Mirrors the backend rule (cf. ``apps/office/services/occurrence_actions.py``
 * Slice B + Slice F.15):
 *
 *   isOverdue  = status == 'scheduled'
 *                AND start + OVERDUE_GRACE_MINUTES < now
 *                AND isMobileTracked
 *                AND NOT isExternallyHandled
 *   isOvertime = status in ('en_route','arrived','in_progress')
 *                AND end < now
 *
 * Required so a card flips from blue → red the moment its
 * ``scheduled_start`` passes the grace window without waiting for a
 * full refetch — but ONLY for mobile-tracked, non-externally-handled
 * visits. Office-only visits (``mobile_tracking_disabled=True``) and
 * externally-handled visits (CIaO scope + 100 % USES_OWN_SYSTEM
 * worksite) stay neutral past their scheduled start because the
 * office is the closer-of-record on those flows; raising a red
 * "overdue" alert on them is misleading and was the PO bug fixed in
 * Slice F.15 (2026-05-14, Job#103 Green Office screenshot).
 *
 * @param {Object} data
 * @param {string} data.status                — JobOccurrence status string.
 * @param {Date|string|null} [data.start]     — scheduled_start_datetime.
 * @param {Date|string|null} [data.end]       — scheduled_end_datetime.
 * @param {boolean} [data.isMobileTracked=true] — backend ``is_mobile_tracked``.
 * @param {boolean} [data.isExternallyHandled=false] — backend ``is_externally_handled``.
 * @param {number} [data.overdueGraceMs]      — override grace in ms (tests).
 * @returns {{isOverdue: boolean, isOvertime: boolean}}
 */
export function computeTimeFlags(data) {
    const now = new Date();
    const status = data.status;
    const start = data.start ? (data.start instanceof Date ? data.start : new Date(data.start)) : null;
    const end = data.end ? (data.end instanceof Date ? data.end : new Date(data.end)) : null;

    const isScheduled = status === 'scheduled';
    const isActive = status === 'en_route' || status === 'arrived' || status === 'in_progress';

    // Default to the legacy "yes mobile tracking" branch when the flag
    // is missing so this helper stays backward-compatible with any
    // (test) caller that does not pass it. The server payload always
    // carries it (see api.py l. 469 / 479 — Slice D + F).
    const isMobileTracked = data.isMobileTracked !== false;
    const isExternallyHandled = data.isExternallyHandled === true;

    // ``OVERDUE_GRACE_MINUTES`` is exposed via ``window.OVERDUE_THRESHOLD_
    // MINUTES`` (set by the schedule_hub template from the Django setting
    // ``OVERDUE_VISIT_THRESHOLD_MINUTES``). Tests inject the value
    // directly via ``data.overdueGraceMs`` to avoid touching ``globalThis``.
    let overdueGraceMs = data.overdueGraceMs;
    if (typeof overdueGraceMs !== 'number') {
        const winGrace = (typeof window !== 'undefined'
            && Number(window.OVERDUE_THRESHOLD_MINUTES)) || 15;
        overdueGraceMs = winGrace * 60 * 1000;
    }

    return {
        isOverdue: Boolean(
            isScheduled
            && start
            && (now.getTime() - start.getTime()) >= overdueGraceMs
            && isMobileTracked
            && !isExternallyHandled
        ),
        isOvertime: Boolean(isActive && end && end < now),
    };
}

/**
 * Rebuild an event's ``classNames`` array from its time-based flags +
 * status, keeping every non-time-based class (status-*, unassigned,
 * conflict, etc.). Pure helper — no DOM access.
 *
 * @param {string[]} baseClassNames           — current FullCalendar classNames.
 * @param {boolean} isOverdue
 * @param {boolean} isOvertime
 * @returns {string[]} updated array (new object — input is not mutated).
 */
export function buildClassNamesWithFlags(baseClassNames, isOverdue, isOvertime) {
    const names = Array.isArray(baseClassNames) ? baseClassNames.slice() : [];
    const filtered = names.filter(c => c !== 'status-overdue' && c !== 'status-overtime');
    if (isOverdue) filtered.push('status-overdue');
    if (isOvertime) filtered.push('status-overtime');
    return filtered;
}

// ---------------------------------------------------------------------------
// Normalize event props from either snake_case (API) or camelCase (cache)
// ---------------------------------------------------------------------------

export function normalizeProps(raw) {
    if (!raw) return {};
    // ``isMobileTracked`` defaults to ``true`` so callers that
    // forget to pass it (legacy callsites, partial mocks in tests)
    // get the historical behaviour — only an explicit ``false``
    // from the backend toggles the office-only badge / icon.
    return {
        status:            raw.status,
        isOverdue:         raw.is_overdue   ?? raw.isOverdue   ?? false,
        isOvertime:        raw.is_overtime   ?? raw.isOvertime  ?? false,
        isUnassigned:      raw.is_unassigned ?? raw.isUnassigned ?? false,
        hasConflicts:      raw.has_conflicts ?? raw.hasConflicts ?? false,
        hasCleanerAbsence: raw.has_cleaner_absence ?? raw.hasCleanerAbsence ?? false,
        hasBreaks:         raw.has_breaks    ?? raw.hasBreaks   ?? false,
        breakMinutes:      raw.break_minutes ?? raw.breakMinutes ?? 0,
        // ``isOnBreak`` flips true while a cleaner is currently
        // paused (active WorkBreak with no ``ended_at``). The
        // cached ``has_breaks`` only flips true once a break
        // ENDS (the model's ``_sync_occurrence_breaks_minutes``
        // hook only fires on save with a populated ``ended_at``),
        // so without this separate flag the calendar / team icon
        // would only appear AFTER the cleaner ends the pause —
        // exactly the bug the PO reported.
        isOnBreak:         raw.is_on_break   ?? raw.isOnBreak   ?? false,
        isMobileTracked:   raw.is_mobile_tracked ?? raw.isMobileTracked ?? true,
        // Slice F § 7.7 — externally-handled flag from the API.
        // Defaults to false to preserve backward compatibility on
        // legacy callers / tests that don't pass it.
        isExternallyHandled: raw.is_externally_handled ?? raw.isExternallyHandled ?? false,
    };
}

// ---------------------------------------------------------------------------
// Status icons (small inline icons shown on the event card next to the time)
// ---------------------------------------------------------------------------

export function getStatusIcons(np, sizeClass = 'w-3.5 h-3.5') {
    const isDone = np.status === 'done';
    const icons = [];

    if (np.isOverdue) icons.push(renderIcon('overdue', sizeClass));
    if (np.isOvertime) {
        const overtimeStatusIcon = np.status === 'en_route' ? 'enRoute'
            : np.status === 'arrived' ? 'arrived' : 'inProgress';
        icons.push(renderIcon(overtimeStatusIcon, sizeClass));
        icons.push(renderIcon('overtime', sizeClass));
    } else if (np.status === 'en_route') {
        icons.push(renderIcon('enRoute', sizeClass));
    } else if (np.status === 'arrived') {
        icons.push(renderIcon('arrived', sizeClass));
    } else if (np.status === 'in_progress') {
        icons.push(renderIcon('inProgress', sizeClass));
    } else if (isDone) {
        icons.push(renderIcon('done', sizeClass));
    }
    if (np.hasCleanerAbsence) icons.push(renderIcon('absence', sizeClass));
    // ``hasBreaks`` lights up once a pause has been *completed*
    // (cached ``breaks_minutes > 0``); ``isOnBreak`` lights up the
    // instant a cleaner taps "Prendre une pause". Either one is a
    // legitimate reason to paint the pause marker on the calendar
    // / team card — without the second flag, the icon was invisible
    // for the entire duration of the live break (PO report).
    if ((np.hasBreaks || np.isOnBreak) && !isDone) {
        icons.push(renderIcon('breakPause', sizeClass));
    }
    if (np.hasConflicts) icons.push(renderIcon('conflict', sizeClass));
    // Slice F § 7.7 — externally-handled visits are unassigned by
    // design (the picker is locked at the form layer, the office
    // closes them manually). Suppress the alarmist yellow
    // "Unassigned" warning icon for them — the discreet
    // ``officeOnly`` marker below carries the right signal.
    if (np.isUnassigned && !np.isExternallyHandled) icons.push(renderIcon('unassigned', sizeClass));
    // Slice D + F — discreet office-only marker. We still paint it
    // on DONE / CANCELLED / NO_SHOW rows so a reviewer scanning
    // history can tell at a glance which visits were tracked vs.
    // closed manually by the office (no harm: the existing status
    // icons stay primary, this one stacks at the end of the row).
    if (!np.isMobileTracked) icons.push(renderIcon('officeOnly', sizeClass));

    return icons;
}

// ---------------------------------------------------------------------------
// Status badges (small colored pills shown in tooltips)
// ---------------------------------------------------------------------------

export function getStatusBadges(np, T) {
    const isDone = np.status === 'done';
    let badges = [];

    // Primary status
    if (np.status === 'en_route') {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">${T.statusEnRoute || 'En Route'}</span>`);
    } else if (np.status === 'arrived') {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-cyan-100 text-cyan-800">${T.statusArrived || 'Arrived'}</span>`);
    } else if (np.status === 'in_progress') {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">${T.statusInProgress || 'In Progress'}</span>`);
    } else if (isDone) {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">${T.statusDone || 'Done'}</span>`);
    } else if (np.status === 'cancelled') {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">${T.statusCancelled || 'Cancelled'}</span>`);
    } else if (np.status === 'no_show') {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">${T.statusNoShow || 'No Show'}</span>`);
    }

    // Modifiers
    if (np.isOverdue) {
        badges = [`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">${T.statusOverdue || 'Overdue'}</span>`];
    } else if (np.isOvertime) {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-800">${T.statusOvertime || 'Overtime'}</span>`);
    }

    if (np.hasConflicts && !np.isOverdue) {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-800">${T.statusConflict || 'Conflict'}</span>`);
    }
    // Slice F § 7.7 — same suppression as the icon list above:
    // externally-handled visits are unassigned by design, the
    // dedicated "Externally handled" badge below carries the
    // correct signal.
    if (np.isUnassigned && !np.isOverdue && !np.isExternallyHandled) {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">${T.statusUnassigned || 'Unassigned'}</span>`);
    }
    if ((np.hasBreaks || np.isOnBreak) && !isDone) {
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-orange-50 text-orange-700">${T.statusBreak || 'Break'}</span>`);
    }
    // Slice D — pair the calendar card icon with a tooltip badge
    // so the office gets the explanation on hover without having
    // to open the drawer. Blue palette mirrors the in-drawer
    // banner shipped in Slice C.4 (see _detail_drawer.html).
    //
    // Slice F § 7.7 — when the visit is externally-handled, swap
    // the generic "Office-only" copy for the more informative
    // "Externally handled" so the office understands the lock came
    // from the worksite's subcontractor configuration (and not a
    // manual office choice). The icon stays the same — same blue
    // building marker — only the tooltip text changes.
    if (!np.isMobileTracked) {
        const officeOnlyLabel = np.isExternallyHandled
            ? (T.statusExternallyHandled || 'Externally handled')
            : (T.statusOfficeOnly || 'Office-only');
        badges.push(`<span class="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800">${officeOnlyLabel}</span>`);
    }

    return badges;
}

// ---------------------------------------------------------------------------
// Tooltip HTML (hover popup with full visit details)
// ---------------------------------------------------------------------------

export function buildTooltipHtml(tooltipId, data, T) {
    const np = normalizeProps(data);
    const statusBadge = getStatusBadges(np, T).join(' ');
    const isDone = np.status === 'done';

    // Tooltip break row: prefer the LIVE "on break" wording over the
    // historical "X min" total when a pause is active right now,
    // because "Break: 0 min" while the cleaner is literally paused
    // reads as broken state. Cleanup-then-resume cases still show
    // the cumulative minutes via ``hasBreaks``.
    const breakMinutesValue = data.breakMinutes ?? np.breakMinutes;
    let breakRow = '';
    if (np.isOnBreak && !isDone) {
        breakRow = `<div class="flex items-center gap-1 mt-1 text-orange-600 dark:text-orange-400">
               <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
               ${T.statusBreak || 'Break'}${breakMinutesValue > 0 ? ` (${breakMinutesValue} min)` : ''}
           </div>`;
    } else if (np.hasBreaks && !isDone) {
        breakRow = `<div class="flex items-center gap-1 mt-1 text-orange-600 dark:text-orange-400">
               <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
               ${T.breakDuration || 'Break'}: ${breakMinutesValue} min
           </div>`;
    }

    return `
        <div id="${tooltipId}" role="tooltip" class="absolute z-[100] invisible inline-block max-w-xs px-3 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 tooltip dark:bg-gray-800 dark:text-white dark:border-gray-600 transition-opacity duration-300">
            <div class="space-y-1.5">
                <div class="font-semibold border-b border-gray-200 dark:border-gray-600 pb-1.5 flex items-center justify-between gap-2">
                    <span>${data.customer || ''}</span>
                    ${statusBadge}
                </div>
                <div class="text-gray-600 dark:text-gray-300 text-xs">
                    <div class="flex items-center gap-1">
                        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/></svg>
                        ${data.location || T.noLocation || 'No location'}
                    </div>
                    <div class="flex items-center gap-1 mt-1">
                        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>
                        ${data.timeRange} (${data.durationMins}min)
                    </div>
                    <div class="flex items-center gap-1 mt-1">
                        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/></svg>
                        ${data.serviceType || T.cleaning || 'Cleaning'}
                    </div>
                    <div class="flex items-center gap-1 mt-1">
                        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/></svg>
                        <span class="${data.isUnassigned ?? np.isUnassigned ? 'text-gray-400 dark:text-gray-500' : ''}">${data.cleanerNames || '\u2014'}</span>
                    </div>
                    ${breakRow}
                </div>
                <div class="text-xs text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700">
                    ${T.clickForDetails || 'Click for details'}
                </div>
            </div>
            <div class="tooltip-arrow" data-popper-arrow></div>
        </div>
    `;
}

// ---------------------------------------------------------------------------
// Status classes — returns a single CSS class name from app.css
// (.event-card-* are the single source of truth for card colors)
// Priority: Overdue > Absence > Conflict > Overtime > Status
// ---------------------------------------------------------------------------

export function getStatusClasses(raw) {
    const np = normalizeProps(raw);
    if (np.isOverdue)                return 'event-card-overdue';
    if (np.hasCleanerAbsence)        return 'event-card-absence';
    if (np.hasConflicts)             return 'event-card-conflict';
    if (np.isOvertime)               return 'event-card-overtime';
    if (np.status === 'en_route')    return 'event-card-en_route';
    if (np.status === 'arrived')     return 'event-card-arrived';
    if (np.status === 'in_progress') return 'event-card-in_progress';
    if (np.status === 'done')        return 'event-card-done';
    if (np.status === 'cancelled')   return 'event-card-cancelled';
    if (np.status === 'no_show')     return 'event-card-no_show';
    return 'event-card-scheduled';
}

// ---------------------------------------------------------------------------
// Draggable check
// ---------------------------------------------------------------------------

export function isDraggable(raw) {
    const np = normalizeProps(raw);
    return np.status === 'scheduled'
        && !np.isOverdue
        && !np.hasCleanerAbsence
        // Externally-handled visits carry an informational read-only
        // subcontractor agent. They must never be drag-reassigned onto
        // one of our agents' lanes — the agent is changed via the drawer
        // (scoped to the worksite's subcontractor) instead.
        && !np.isExternallyHandled;
}
